import { useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  PageViewContext,
  PageViewTypeEnum,
  PageViewToggle,
  Text,
  PaginationContext,
  SearchContext,
  ModalContext,
  Button,
  FilterContext,
} from 'dash-ui';
import {
  useGetPortfoliosQuery,
  useObservePropertiesChangesSubscription,
} from 'gql/generated.graphql';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { RoutesPath } from 'flow-components/routes/routes';

import PortfoliosListCardView from './components/PortfoliosListCardView/PortfoliosListCardView';
import PortfoliosListTableView from './components/PortfoliosListTableView/PortfoliosListTableView';

const PortfoliosPageView = () => {
  const theme = useTheme();
  const navigation = useNavigate();
  const { t } = useTranslation('pages');
  const { viewType } = useContext(PageViewContext);
  const { setTotal, state, getRefetchProps } = useContext(PaginationContext);
  const { state: filterState } = useContext(FilterContext);
  const { sort = {} } = filterState;
  const { isOpenCurrent } = useContext(ModalContext);
  const { searchSlug } = useContext(SearchContext);
  const searchText = searchSlug.length > 2 ? searchSlug : null;
  const { skip, limit } = getRefetchProps(state);
  const { data, loading, refetch } = useGetPortfoliosQuery({
    variables: {
      searchText,
      pagination: { skip, limit },
      sort:
        (sort.sortOptions || []).length > 0
          ? sort.sortOptions
          : [{ fieldName: 'name', sortOrder: 1 }],
    },
    onCompleted: (result) => {
      const total = result?.getPortfolios.totalCount || 0;
      setTotal(total);
    }
  });

  const { portfolios, totalCount } = useMemo(
    () => ({
      portfolios: data?.getPortfolios.collection || [],
      totalCount: data?.getPortfolios.totalCount || 0,
    }),
    [data]
  );

  useObservePropertiesChangesSubscription();

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenCurrent]);

  const ViewComponent =
    viewType === PageViewTypeEnum.LIST
      ? PortfoliosListTableView
      : PortfoliosListCardView;

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Text>
          {t('portfoliosPage.portfoliosTotal', { total: portfolios.length })}
        </Text>
        <PageViewToggle
          defaultButton={PageViewTypeEnum.CARDS}
          buttons={[
            { icon: 'apps', viewType: PageViewTypeEnum.CARDS },
            { icon: 'list', viewType: PageViewTypeEnum.LIST },
          ]}
        />
      </Flex>
      {!totalCount && !loading ? (
        <Flex
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          h={500}
        >
          {searchSlug ? (
            <Text
              color={theme.colors.n80}
            >{t`common:placeholders.noSearchResults`}</Text>
          ) : (
            <>
              <Text semibold lg>
                {t('portfoliosPage.emptyPageLabel')}
              </Text>
              <Text color={theme.colors.n80}>
                {t('portfoliosPage.emptyPageSubLabel')}
              </Text>
              <Button
                mt={4}
                onClick={() => {
                  navigation(RoutesPath.Properties);
                }}
              >
                {t('portfoliosPage.emptyPageButtonLabel')}
              </Button>
            </>
          )}
        </Flex>
      ) : (
        <Box>
          <ViewComponent portfolios={portfolios} loading={loading} />
        </Box>
      )}
    </Box>
  );
};

export default PortfoliosPageView;
